import { parseActorAppearance, type ActorAppearancePreferences } from "@neucleos/theme";
import { postLocalJson } from "./localJsonTransport";
import { isToolNotOfferedRefusal } from "./refusalCopy";
export type AppearanceOutcome =
  | {
      ok: true;
      actorId: string;
      updatedAt: number | null;
      preferences: ActorAppearancePreferences | null;
    }
  | { ok: false; reason: string };
async function call(
  worldKey: string,
  preferences: ActorAppearancePreferences | undefined,
  updatedAt: number | null,
  fetchImpl: typeof fetch,
): Promise<AppearanceOutcome> {
  try {
    const response = await postLocalJson(
      `/api/neucleos/appearance${preferences ? "/save" : ""}`,
      {
        worldKey,
        ...(preferences ? { preferences: parseActorAppearance(preferences), updatedAt } : {}),
      },
      fetchImpl,
    );
    const value = (await response.json()) as Record<string, unknown>;
    if (!response.ok || value.ok !== true)
      return {
        ok: false,
        reason: isToolNotOfferedRefusal(value.kind)
          ? "Appearance is stored on this device. This World doesn't offer account sync for it yet; sync will start when the World is updated. You don't need to sign in again."
          : value.kind === "not_signed_in"
            ? "Appearance is stored on this device. Sign in again to enable account sync."
            : typeof value.reason === "string" && value.reason.includes("appearance_stale")
              ? "Appearance changed on another device. Retry sync to apply your latest choices."
              : "Appearance is stored on this device; account sync is unavailable. Retry when connected.",
      };
    if (
      typeof value.actorId !== "string" ||
      (value.updatedAt !== null &&
        (typeof value.updatedAt !== "number" || !Number.isFinite(value.updatedAt))) ||
      !(
        value.preferences === null ||
        (typeof value.preferences === "object" && !Array.isArray(value.preferences))
      )
    )
      return { ok: false, reason: "Appearance readback was invalid." };
    const stored = value.preferences === null ? null : parseActorAppearance(value.preferences);
    if (preferences && JSON.stringify(stored) !== JSON.stringify(parseActorAppearance(preferences)))
      return { ok: false, reason: "Appearance readback did not match your choices." };
    return {
      ok: true,
      actorId: value.actorId,
      updatedAt: value.updatedAt as number | null,
      preferences: stored,
    };
  } catch {
    return { ok: false, reason: "Appearance is saved on this device; the account is unreachable." };
  }
}
export const readAppearance = (worldKey: string, fetchImpl: typeof fetch = fetch) =>
  call(worldKey, undefined, null, fetchImpl);
export const saveAppearance = (
  worldKey: string,
  preferences: ActorAppearancePreferences,
  updatedAt: number | null,
  fetchImpl: typeof fetch = fetch,
) => call(worldKey, preferences, updatedAt, fetchImpl);
